import { boolean, number } from 'yup';

import {
  MaritalStatusEnum,
  RelationToBuyerEnum,
  StateAbbreviation,
} from '../../gql/generated/graphql';

import { emailValidationRequired } from './email';
import { conditionalPhoneNumberValidation, phoneNumberValidationOptional } from './phoneNumber';
import { ssnValidation } from './ssn';
import { nameValidation, optionalStringValidation } from './strings';

import { countryRulesFor } from '../../constants/countries';
import { dobValidation } from '../../libs/yup-validators/dob';

export const personNameValidation = (person?: string) => ({
  first_name: nameValidation(person, 'First '),
  last_name: nameValidation(person, 'Last '),
});

const personContactValidation = (person?: string) => ({
  phone_number: conditionalPhoneNumberValidation(person),
  home_phone_number: phoneNumberValidationOptional(person),
  email: emailValidationRequired(person),
});

// The address country wins for a new estimate; the credit app holds the deal country on the
// form root, which Formik passes to Yup as the context.
const personSsnValidation = (person?: string) => {
  const { ssn_id, has_full_ssn } = ssnValidation(person);
  return {
    ssn_id: number()
      .nullable()
      .when(['address.country', '$country'], {
        is: (addressCountry?: string | null, dealCountry?: string | null) =>
          !countryRulesFor(addressCountry ?? dealCountry).requiresSsn,
        then: number().nullable(),
        otherwise: ssn_id,
      }),
    has_full_ssn,
  };
};

export const personInfoValidation = (person?: string) => ({
  ...personNameValidation(person),
  ...personContactValidation(person),
  ...personSsnValidation(person),
  dob: dobValidation(person),
  marital_status: optionalStringValidation.test(
    'maritalStatusRequired',
    'Marital Status is required',
    (value, context) => {
      if (context?.parent?.address?.state === StateAbbreviation.Wi) {
        return !!value;
      }

      return true;
    },
  ),
  wi_notification_agreement: boolean().test(
    'cobuyer.wi_notification_agreement',
    'Cobuyer Credit Acknowledgement is required',
    function test(value) {
      const isCobuyer = person === 'Cobuyer ';
      if (!isCobuyer) {
        return true;
      }

      const { address, relation_to_buyer, marital_status } = this.options.context?.cobuyer || {};
      if (
        address?.state !== StateAbbreviation.Wi ||
        relation_to_buyer !== RelationToBuyerEnum.Spouse ||
        ![MaritalStatusEnum.Married, MaritalStatusEnum.Separated].includes(marital_status)
      ) {
        return true;
      }

      return !!value;
    },
  ),
});
